import {inject, Service} from '@angular/core'
import {environment} from '../../environments/environment'
import {catchError, map, switchMap, takeWhile, tap} from 'rxjs/operators'
import {HttpClient, HttpErrorResponse} from '@angular/common/http'
import {BehaviorSubject, forkJoin, NEVER, Observable, of, Subject, timer} from 'rxjs'
import {ILoanListItem} from '@sparbanken-syd/renewal-tool'
import {IHistoryItem, IInterests, ILoan, ILoanDbListItem, ILoanOvItem} from '@sparbanken-syd/loan-renewal-be'
import {LetterSelector} from './letter-selector/letter-selector'
import {MatDialog} from '@angular/material/dialog'
import {IWaitComponentData, WaitComponent} from '@sparbanken-syd/sparbanken-syd-theme'

export interface LoanListItem extends ILoanOvItem {
  loanNumber: string
  // Owner name
  owner: string
  // Customer number (person/org number)
  sub: string
  // Loan amount
  amount: number
  renewalDate: number
  // Used internally only
  selected: boolean
  responsible: string
  office: string
  alive: boolean
  //
  frequency: string
  newLoanBinding?: string
  stringDate: string // This is added so that we can search on dates.
  interest: number
  interestDeviation: number
  ownerType: string
  /**
   * Actually one of l751, l752, l753 etc. Fix that
   */
  productCode: string
  /**
   * All dates that are not 1st day of the month should be marked as such
   */
  badDate: boolean
  // Internet possible
  ib: boolean
  /**
   * If this item is in saving mode
   */
  saving?: boolean
}

export interface LoanResponse extends ILoan {

  id: string

  timeStamp: number

  printed: LoanPrintStatus[]

}

export interface LoanPrintStatus {
  date: number
  orderedBy: string
  type: string // Think Manual/Postnord
}

export interface ConditionsUrl {
  id: string // Either uuidV4 or a combination of loanNumber_customerId.pdf
  loanNumber: string // The sparbanken loan like 5432654322	52 5146 0407
  customerId: string // Like personnummer
  url: string
  downloaded?: boolean
}

export interface PrintResponse {

  /**
   * The id of the request
   */
  id: string

  /**
   * Status, should be 'CREATED', 'MISSING' or 'COMPLETE'
   */
  status: 'CREATED' | 'MISSING' | 'COMPLETE' | 'FAILED'

  /**
   * A timed URL for the main document
   */
  url: string

  /**
   * A list of urls to "Villkorsbilagor"
   */
  conditionsUrls: ConditionsUrl[]
}

export interface LoanSendStatus {
  ob: number | null
  vb: number | null
  l: string
}

export interface IHagelDate {
  ready: boolean
  // Unix epoch time ms
  time: number
}

@Service()
export class DataService {

  /**
   * Outsider list of loans
   */

  public loans$: BehaviorSubject<LoanListItem[]> = new BehaviorSubject<LoanListItem[]>([])

  public history$ = new Subject<string>()

  public readonly loansLoaded$ = new BehaviorSubject<boolean>(false)

  private readonly http = inject(HttpClient)

  private readonly selector = inject(LetterSelector)

  private readonly dialog = inject(MatDialog)

  /**
   * Fetch all loans, these will be heavily cached
   */
  public updateLoanList(force: boolean): Observable<LoanListItem[]> {
    // Do not reload loans on every view switch.
    if (this.loansLoaded$.value && !force) {
      return of(this.loans$.value)
    }
    const bust = force ? `?ts=${Date.now()}` : ''
    return forkJoin([
      this.http.get<ILoanListItem[]>(`${environment.dataServiceUrl}/loans${bust}`),
      this.http.get<ILoanOvItem[]>(`${environment.dataServiceUrl}/meta`),
      this.http.get<IHagelDate>(`${environment.dataServiceUrl}/date`)
    ]).pipe(
      map(([loans, meta, date]) => {
        const items = this.processLoanData(loans, meta)
        this.selector.hagelDate.set(new Date(date.ready ? date.time : 0).toLocaleDateString('sv-SE'))
        this.loans$.next(items)
        this.loansLoaded$.next(true)
        return this.loans$.value
      }),
      catchError(this.handleError)
    )
  }

  /**
   * Fetch the details for one loan, decorated with _some_ meta,
   * start date and last villkorsändringsdag.
   */
  public getLoanDetails(loanNumber: string): Observable<LoanResponse> {
    const url = `${environment.dataServiceUrl}/loans/${loanNumber}`
    return this.http.get<LoanResponse>(url).pipe(
      catchError(this.handleError)
    )
  }

  public getHistory(loanNumber: string): Observable<IHistoryItem[]> {
    const url = `${environment.dataServiceUrl}/history/${loanNumber}`
    return this.http.get<IHistoryItem[]>(url)
  }

  /**
   * Print one or more documents
   *
   * @param ids - A list of guids to print
   * @param type 'O' - Omsättningsbrev or 'V' Villkorsbilaga.
   */
  public print(ids: string[], type: 'O' | 'V'): Observable<PrintResponse> {
    const url = `${environment.dataServiceUrl}/print`
    return this.http.put<PrintResponse>(url, {ids, create: true, type})
  }

  public send(ids: string[], type: 'O' | 'V'): Observable<PrintResponse> {
    const url = `${environment.dataServiceUrl}/send`
    return this.http.put<PrintResponse>(url, {ids, create: true, type})
      .pipe(tap(() => this.checkStatus(ids, type)))
  }

  /**
   * Get the result of the print operation
   *
   * @param id - The id of the request
   */
  public getPrint(id: string): Observable<PrintResponse> {
    const url = `${environment.dataServiceUrl}/print/${id}`
    return this.http.get<PrintResponse>(url)
  }

  public saveAdminData(meta: Partial<ILoanDbListItem>): Observable<void> {
    const url = `${environment.dataServiceUrl}/meta/${meta.l}`
    return this.http.put<void>(url, meta)
      .pipe(tap(() => {
        this.loans$.value
          .filter(l => l.l === meta.l)
          .forEach(l => Object.assign(l, meta))
        this.history$.next(meta.l!)
        this.loans$.next(this.loans$.value)
      }))
  }

  public getInterests() {
    const url = `${environment.dataServiceUrl}/interests`
    return this.http.get<IInterests>(url)
  }

  /**
   * This is there so that you can free text search for dates like 20241201
   * @param timeStamp
   * @private
   */
  private createFakeDateString(timeStamp: number): string {
    let fullDate = new Date(timeStamp).toLocaleDateString('sv-SE')
    fullDate += fullDate.replaceAll('-', '')
    return fullDate
  }

  private checkStatus(ids: string[], type: 'O' | 'V') {
    const start = Date.now()
    const url = `${environment.dataServiceUrl}/send/status`
    const prop = `${type}b`.toLowerCase()
    timer(3000, 3000).pipe(
      takeWhile(() => ids.length > 0),
      switchMap(() => this.http.put<LoanSendStatus[]>(url, {ids, type}))
    ).subscribe(res => {
      const complete = res.filter(r => {
        return r[prop] !== null && r[prop] > start
      })

      const completeIds = new Set(complete.map(r => r.l))

      ids = ids.filter(id => !completeIds.has(id))

      const toUpdate = this.loans$
        .value.filter(l => completeIds.has(l.l))

      toUpdate.forEach(u => {
        u[prop] = (complete.find(c => c.l === u.l))![prop]
      })
    })
  }

  private processLoanData(loans: ILoanListItem[], meta: ILoanOvItem[]): LoanListItem[] {
    const metaMap = new Map<string, ILoanOvItem>(meta.map(m => [m.l, m]))
    return loans
      // Hide all loans that have no amortization left?
      .filter((lr: ILoanListItem) => lr.a > 0)
      .map((lr: ILoanListItem): LoanListItem => {
        //const selected: boolean = lr.printed !== undefined
        //const adminData: ILoanAdminData = lr.adminData || {}
        //const renewalDate = new Date(lr.loan.actualStartDate || lr.loan.renewalDate).getTime()
        // The assumption here that all business loans are business customers.
        const ownerType = ['l760', 'l770', 'l765', 'l771'].includes(lr.c) ? 'B' : 'P'
        // lr.adminData = adminData
        const meta = metaMap.get(lr.l)
        const res: LoanListItem = {
          sub: lr.s,
          as: null,
          l: lr.l,
          ob: null,
          pvd: null,
          v: lr.v,
          vb: null,
          vd: null,
          loanNumber: lr.l,
          owner: lr.n,
          // Wrong
          renewalDate: lr.v, // Villkorsändringsdag
          amount: lr.a,
          responsible: this.selector.valueOrMissing(lr, 'e'),
          office: this.selector.valueOrMissing(lr, 'o'),
          alive: !!lr.d,
          frequency: lr.b,
          selected: false,

          stringDate: this.createFakeDateString(lr.v),
          interest: lr.r,
          interestDeviation: lr.f,
          ownerType,
          productCode: lr.c,
          badDate: false,
          ib: lr.i,
          s: lr.b
        }
        if (!meta) {
          return res
        }
        res.renewalDate = meta.v
        res.pvd = meta.pvd
        res.ob = meta.ob
        res.vb = meta.vb
        res.nb = meta.nb
        res.nd = meta.nd
        res.rk = meta.rk
        res.vd = meta.vd
        res.ex = meta.ex
        res.ds = meta.ds
        res.db = meta.db
        return res
      })
  }

  private readonly handleError = (err: HttpErrorResponse) => {

    this.dialog.closeAll()
    if (err.status === 409) {
      this.dialog.open<WaitComponent, IWaitComponentData, any>(WaitComponent, {
        data: {
          title: 'Uppdatering pågår',
          text: 'Vänta en stund, prova sedan igen genom att uppdatera.',
          closable: true,
          buttonStyle: 'filled'
        }
      })
    }
    return NEVER
  }
}
